/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable no-console */
/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import { dispatch } from '@wordpress/data';
import apiFetch from '@wordpress/api-fetch';

/**
 * Internal dependencies
 */
import { PRODUCT_HOTSPOT_CONSTANTS } from '../../utils/constants';

/**
 * WooCommerce Layer Manager
 * Handles WooCommerce layer functionality including creation, positioning, and interaction
 */
export default class WooCommerceLayerManager {
	static BASE_WIDTH = PRODUCT_HOTSPOT_CONSTANTS.BASE_WIDTH;
	static BASE_HEIGHT = PRODUCT_HOTSPOT_CONSTANTS.BASE_HEIGHT;

	constructor( player, isDisplayingLayers, currentPlayerVideoInstanceId ) {
		this.player = player;
		this.wooLayers = [];
		this.wasPlayingBeforeHover = false;
		this.isDisplayingLayers = isDisplayingLayers;
		this.currentPlayerVideoInstanceId = currentPlayerVideoInstanceId;

		// Product data cache (per player instance) to keep hotspots instant.
		this.productCache = new Map();
		this.productFetchPromises = new Map();
		this.didBindPrefetchHandlers = false;
		this.didPrefetchProducts = false;

		this.bindMiniCartFullscreenFix();

		if ( typeof this.player?.on === 'function' ) {
			this.player.on( 'dispose', () => {
				this.disposeMiniCartFullscreenFix();
			} );
		}
	}

	bindProductPrefetchHandlers() {
		if ( this.didBindPrefetchHandlers ) {
			return;
		}
		this.didBindPrefetchHandlers = true;

		const prefetch = () => {
			this.prefetchAllProducts();
		};

		// Prefetch as soon as metadata is available (video loaded, not necessarily played).
		if ( typeof this.player?.one === 'function' ) {
			this.player.one( 'loadedmetadata', prefetch );
			this.player.one( 'loadeddata', prefetch );
			this.player.one( 'canplay', prefetch );
		}

		// Also prefetch on first user intent (hover/click), in case events above don't fire.
		const el = this.player?.el?.();
		if ( el ) {
			el.addEventListener( 'pointerenter', prefetch, { once: true, passive: true } );
			el.addEventListener( 'touchstart', prefetch, { once: true, passive: true } );
			el.addEventListener( 'mousedown', prefetch, { once: true, passive: true } );
		}
	}

	getAllUniqueProductIds() {
		const ids = new Set();
		this.wooLayers.forEach( ( layerObj ) => {
			( layerObj.productHotspots || [] ).forEach( ( hotspot ) => {
				if ( hotspot?.productId ) {
					ids.add( hotspot.productId );
				}
			} );
		} );
		return [ ...ids ];
	}

	fetchProductById( productId ) {
		if ( ! productId ) {
			return Promise.resolve( null );
		}

		if ( this.productCache.has( productId ) ) {
			return Promise.resolve( this.productCache.get( productId ) );
		}

		if ( this.productFetchPromises.has( productId ) ) {
			return this.productFetchPromises.get( productId );
		}

		const promise = apiFetch( {
			url: `${ window.godamRestRoute?.url || '' }godam/v1/wcproduct?id=${ productId }`,
		} )
			.then( ( product ) => {
				if ( product ) {
					this.productCache.set( productId, product );
				}
				return product || null;
			} )
			.catch( () => null )
			.finally( () => {
				this.productFetchPromises.delete( productId );
			} );

		this.productFetchPromises.set( productId, promise );
		return promise;
	}

	fetchProductsByIds( productIds ) {
		const ids = ( productIds || [] )
			.map( ( id ) => parseInt( id, 10 ) )
			.filter( ( id ) => Number.isFinite( id ) && id > 0 );

		if ( ids.length === 0 ) {
			return Promise.resolve( [] );
		}

		// If everything is already cached, avoid a request.
		const uncached = ids.filter( ( id ) => ! this.productCache.has( id ) && ! this.productFetchPromises.has( id ) );
		if ( uncached.length === 0 ) {
			// Return cached data or wait for in-flight requests
			return Promise.all(
				ids.map( ( id ) => {
					if ( this.productCache.has( id ) ) {
						return Promise.resolve( this.productCache.get( id ) );
					}
					if ( this.productFetchPromises.has( id ) ) {
						return this.productFetchPromises.get( id );
					}
					return Promise.resolve( null );
				} ),
			);
		}

		// Mark per-id promises so fetchProductById can piggy-back.
		const batchPromise = apiFetch( {
			url: `${ window.godamRestRoute?.url || '' }godam/v1/wcproducts-by-ids`,
			method: 'POST',
			data: { ids: uncached },
		} ).catch( ( error ) => {
			console.warn( 'Batch product fetch failed, individual requests will be used:', error );
			return [];
		} );

		uncached.forEach( ( id ) => {
			if ( ! this.productFetchPromises.has( id ) ) {
				this.productFetchPromises.set(
					id,
					batchPromise.then( ( list ) => ( list || [] ).find( ( p ) => p?.id === id ) || null ),
				);
			}
		} );

		return batchPromise
			.then( ( products ) => {
				( products || [] ).forEach( ( product ) => {
					if ( product?.id ) {
						this.productCache.set( product.id, product );
					}
				} );
				return products || [];
			} )
			.finally( () => {
				uncached.forEach( ( id ) => this.productFetchPromises.delete( id ) );
			} );
	}

	prefetchAllProducts() {
		if ( this.didPrefetchProducts ) {
			return;
		}

		// Respect data-saver mode if present.
		if ( window?.navigator?.connection?.saveData ) {
			return;
		}

		this.didPrefetchProducts = true;
		const productIds = this.getAllUniqueProductIds();
		if ( productIds.length === 0 ) {
			return;
		}

		const run = () => {
			this.fetchProductsByIds( productIds );
		};

		if ( typeof window.requestIdleCallback === 'function' ) {
			window.requestIdleCallback( run, { timeout: 1500 } );
		} else {
			window.setTimeout( run, 0 );
		}
	}

	/**
	 * Setup WooCommerce layer
	 *
	 * @param {Object}      layer        - Layer configuration object
	 * @param {HTMLElement} layerElement - Layer DOM element
	 */
	setupWooCommerceLayer( layer, layerElement ) {
		const layerObj = {
			layerElement,
			displayTime: parseFloat( layer.displayTime ),
			duration: layer.duration ? parseInt( layer.duration ) : 0,
			show: true,
			productHotspots: layer.productHotspots || [],
			pauseOnHover: layer.pauseOnHover || false,
			miniCart: layer.miniCart || false,
		};

		this.wooLayers.push( layerObj );
		this.bindProductPrefetchHandlers();
	}

	// Layer registry interface methods — called by layersManager for custom layers.
	setupLayer( layer, layerElement ) {
		this.setupWooCommerceLayer( layer, layerElement );
	}

	handleTimeUpdate( currentTime ) {
		this.handleWooCommerceLayersTimeUpdate( currentTime );
	}

	handleResize() {
		this.updateProductHotspotPositions();
	}

	/**
	 * Handle WooCommerce layers time update
	 *
	 * @param {number} currentTime - Current video time in seconds
	 */
	handleWooCommerceLayersTimeUpdate( currentTime ) {
		const blockedByLayer = this.isDisplayingLayers?.[ this.currentPlayerVideoInstanceId ] === true;

		this.wooLayers.forEach( ( layerObj ) => {
			if ( ! layerObj.show ) {
				return;
			}

			const endTime = layerObj.displayTime + layerObj.duration;
			const isActive = currentTime >= layerObj.displayTime && currentTime < endTime;

			if ( blockedByLayer ) {
				if ( ! layerObj.layerElement.classList.contains( 'overlapped' ) ) {
					layerObj.layerElement.classList.add( 'overlapped' );
				}
			} else if ( layerObj.layerElement.classList.contains( 'overlapped' ) ) {
				layerObj.layerElement.classList.remove( 'overlapped' );
			}

			if ( isActive ) {
				if ( layerObj.layerElement.classList.contains( 'hidden' ) ) {
					layerObj.layerElement.classList.remove( 'hidden' );
					if ( ! layerObj.layerElement.dataset?.productHotspotsInitialized ) {
						this.createProductHotspots( layerObj );
						layerObj.layerElement.dataset.productHotspotsInitialized = true;
					} else {
						requestAnimationFrame( () => {
							const hotspotDivs = layerObj.layerElement.querySelectorAll( '.hotspot' );
							hotspotDivs.forEach( ( hotspotDiv ) => {
								const tooltipDiv = hotspotDiv.querySelector( '.hotspot-tooltip' );
								if ( tooltipDiv ) {
									this.positionTooltip( hotspotDiv, tooltipDiv );
								}
							} );
						} );
					}
				}
			} else if ( ! layerObj.layerElement.classList.contains( 'hidden' ) ) {
				layerObj.layerElement.classList.add( 'hidden' );
			}
		} );
	}

	/**
	 * Handle fullscreen changes for WooCommerce layers
	 *
	 * @param {boolean}     isFullscreen   - Whether player is in fullscreen
	 * @param {HTMLElement} videoContainer - Video container element
	 */
	handleFullscreenChange( isFullscreen, videoContainer ) {
		this.wooLayers.forEach( ( layerObj ) => {
			if ( isFullscreen && ! videoContainer.contains( layerObj.layerElement ) ) {
				videoContainer.appendChild( layerObj.layerElement );

				// Move cart message to fullscreen.
				const msg = document.querySelector( '.mini-cart-product-message' );
				if ( msg ) {
					videoContainer.appendChild( msg );
				}

				const wrapper = videoContainer.parentNode;
				const miniCart = wrapper.querySelector( '.godam-video--cart-basket' );

				if ( miniCart ) {
					videoContainer.appendChild( miniCart );
				}
			}
		} );

		// Use requestAnimationFrame to wait for layout to stabilize after fullscreen resize.
		let framesToWait = 2;
		const waitForResize = () => {
			if ( framesToWait > 0 ) {
				framesToWait--;
				window.requestAnimationFrame( waitForResize );
			} else {
				this.updateProductHotspotPositions();
			}
		};
		window.requestAnimationFrame( waitForResize );
	}

	/**
	 * Handle Mini Cart click in fullscreen
	 * → exit fullscreen → open drawer → wait until close → re-enter fullscreen
	 */
	bindMiniCartFullscreenFix() {
		if ( this._miniCartFsBound ) {
			return;
		}
		this._miniCartFsBound = true;

		const container = this.player?.el?.() || document;

		this._miniCartClickHandler = ( e ) => {
			const trigger = e.target.closest(
				'.wc-block-mini-cart, .godam-video--cart-basket',
			);

			if ( ! trigger ) {
				return;
			}

			const isFullscreen = this.player?.el()?.classList.contains( 'vjs-fullscreen' );
			if ( ! isFullscreen ) {
				return;
			}

			e.preventDefault();
			e.stopPropagation();

			const playerEl = this.player?.el?.();

			const isIOS = /iPhone|iPad|iPod/i.test( navigator.userAgent );

			try {
				const doc = document;

				let didExitFullscreen = false;
				if ( isIOS ) {
					const exitControl = doc.getElementsByClassName(
						'vjs-custom-fullscreen-exit-control',
					)[ 0 ];
					if ( exitControl && typeof exitControl.click === 'function' ) {
						console.log( 'Clicking custom exit control for iOS fullscreen exit' );
						exitControl.click();
						didExitFullscreen = true;
					}
				} else if ( typeof this.player.exitFullscreen === 'function' ) {
					this.player.exitFullscreen();
					didExitFullscreen = true;
				} else if ( typeof this.player.webkitExitFullscreen === 'function' ) {
					this.player.webkitExitFullscreen();
					didExitFullscreen = true;
				}
				if ( ! didExitFullscreen ) {
					return;
				}

				let isProgrammaticClick = false;

				setTimeout( () => {
					if ( isProgrammaticClick ) {
						return;
					}

					isProgrammaticClick = true;
					trigger.click();

					this.showFullscreenReturnMessage();
					this.waitForDrawerCloseAndRestoreFullscreen( playerEl );

					setTimeout( () => {
						isProgrammaticClick = false;
					}, 0 );
				}, 120 );
			} catch ( err ) {
				console.warn( 'Mini cart fullscreen fix failed:', err );
			}
		};

		container.addEventListener( 'click', this._miniCartClickHandler );
	}

	disposeMiniCartFullscreenFix() {
		if ( this._miniCartClickHandler ) {
			const container = this.player?.el?.() || document;
			container.removeEventListener( 'click', this._miniCartClickHandler );
			this._miniCartClickHandler = null;
			this._miniCartFsBound = false;
		}
	}

	/**
	 * Show message to guide user
	 */
	showFullscreenReturnMessage() {
		if ( document.querySelector( '.godam-fs-return-msg' ) ) {
			return;
		}

		const msg = document.createElement( 'div' );
		msg.className = 'godam-fs-return-msg';

		const message = __( 'Close cart & return to fullscreen', 'godam-for-woo' );
		const returnButton = __( 'Return to Fullscreen', 'godam-for-woo' );

		const messageText = document.createTextNode( message );
		const lineBreak = document.createElement( 'br' );

		const btn = document.createElement( 'button' );
		btn.className = 'godam-return-fs-btn';
		btn.textContent = returnButton;

		msg.appendChild( messageText );
		msg.appendChild( lineBreak );
		msg.appendChild( btn );

		document.body.appendChild( msg );

		// Bind click.
		btn.addEventListener( 'click', () => {
			const playerEl = this.player?.el?.();

			const isIOS = /iPhone|iPad|iPod/i.test( navigator.userAgent );

			// 1. Try closing drawer (Woo way).
			const closeBtn = document.querySelector(
				'.wc-block-components-drawer__close',
			);

			if ( closeBtn ) {
				closeBtn.click();
			} else {
				// fallback: click overlay.
				const overlay = document.querySelector(
					'.wc-block-components-drawer__screen-overlay',
				);
				if ( overlay ) {
					overlay.click();
				}
			}

			// 2. Enter fullscreen.
			if ( ! isIOS && playerEl?.requestFullscreen ) {
				playerEl.requestFullscreen();
			} else {
				playerEl.querySelector( '.vjs-fullscreen-control' )?.click();
			}

			// 3. Cleanup message.
			this.cleanupFullscreenMessage();
		} );
	}

	/**
	 * Watches the Woo mini-cart drawer and cleans up the fullscreen return CTA
	 * when the drawer is no longer visible.
	 */
	waitForDrawerCloseAndRestoreFullscreen() {
		const checkDrawer = () => {
			const drawer = document.querySelector(
				'.wc-block-components-drawer__screen-overlay',
			);

			const isOpen =
				drawer &&
				window.getComputedStyle( drawer ).display !== 'none' &&
				window.getComputedStyle( drawer ).visibility !== 'hidden';

			if ( ! isOpen ) {
				this.cleanupFullscreenMessage();
				return;
			}

			requestAnimationFrame( checkDrawer );
		};

		requestAnimationFrame( checkDrawer );
	}

	/**
	 * Remove helper message
	 */
	cleanupFullscreenMessage() {
		const msg = document.querySelector( '.godam-fs-return-msg' );
		if ( msg ) {
			msg.remove();
		}
	}

	/**
	 * Create Product hotspots for WooCommerce layer
	 *
	 * @param {Object} layerObj - Layer object containing Product hotspots and configuration
	 */
	createProductHotspots( layerObj ) {
		const videoContainer = this.player.el();
		const containerWidth = videoContainer?.offsetWidth;
		const containerHeight = videoContainer?.offsetHeight;

		const baseWidth = WooCommerceLayerManager.BASE_WIDTH;
		const baseHeight = WooCommerceLayerManager.BASE_HEIGHT;

		// Get all unique product IDs
		const productIds = [ ...new Set(
			layerObj.productHotspots
				.filter( ( hotspot ) => hotspot.productId )
				.map( ( hotspot ) => hotspot.productId ),
		) ];

		// If no products, return early
		if ( productIds.length === 0 ) {
			return;
		}

		// Track hotspot elements by index for later updates
		const hotspotElements = [];

		// First, create placeholder hotspots (without using old productDetails)
		layerObj.productHotspots.forEach( ( hotspot, index ) => {
			if ( hotspot.productId ) {
				// Create a clean hotspot object without old productDetails
				const cleanHotspot = { ...hotspot, productDetails: null };

				const hotspotDiv = this.createProductHotspotElement( cleanHotspot, containerWidth, containerHeight, baseWidth, baseHeight );

				// Store element reference by index
				hotspotElements[ index ] = hotspotDiv;

				if ( layerObj.pauseOnHover ) {
					this.setupProductHotspotHoverEvents( hotspotDiv );
				}

				layerObj.layerElement.appendChild( hotspotDiv );

				const productBoxDiv = hotspotDiv.querySelector( '.product-hotspot-box' );
				if ( productBoxDiv ) {
					requestAnimationFrame( () => {
						this.positionProductBox( hotspotDiv, productBoxDiv );
					} );
				}
			}
		} );

		// Then fetch all products in a single request and update hotspots
		this.fetchProductsByIds( productIds ).then( ( products ) => {
			const productMap = {};

			// Create a map of productId -> product data
			products.forEach( ( product ) => {
				if ( product ) {
					productMap[ product.id ] = product;
				}
			} );

			// Update hotspots with loaded product data
			layerObj.productHotspots.forEach( ( hotspot, index ) => {
				if ( hotspot.productId && productMap[ hotspot.productId ] ) {
					// Attach fetched product details to hotspot object
					hotspot.productDetails = productMap[ hotspot.productId ];

					// Get the hotspot element we created earlier
					const hotspotEl = hotspotElements[ index ];
					if ( hotspotEl ) {
						// Replace placeholder box with a newly built one (ensures correct markup + handlers)
						const oldProductBoxDiv = hotspotEl.querySelector( '.product-hotspot-box' );
						if ( oldProductBoxDiv ) {
							const newProductBoxDiv = this.createProductHotspotProductBox( hotspot );
							oldProductBoxDiv.replaceWith( newProductBoxDiv );

							// Reposition the product box
							requestAnimationFrame( () => {
								this.positionProductBox( hotspotEl, newProductBoxDiv );
							} );
						}
					}
				}
			} );
		} );
	}

	/**
	 * Compute content rectangle
	 *
	 * @return {Object|null} Content rectangle {left, top, width, height} or null
	 */
	computeContentRect() {
		const videoEl = this.player.tech( true )?.el() || this.player.el().querySelector( 'video' );
		const containerEl = this.player.el();

		if ( ! videoEl || ! containerEl ) {
			return null;
		}

		const nativeW = videoEl.videoWidth || this.player.videoWidth() || 0;
		const nativeH = videoEl.videoHeight || this.player.videoHeight() || 0;

		const elW = containerEl.offsetWidth;
		const elH = containerEl.offsetHeight;

		// If video dimensions aren't loaded yet, use full container
		if ( ! nativeW || ! nativeH ) {
			return {
				left: 0,
				top: 0,
				width: elW,
				height: elH,
			};
		}

		const videoAspectRatio = nativeW / nativeH;
		const containerAspectRatio = elW / elH;

		let contentW, contentH, offsetX, offsetY;

		if ( containerAspectRatio > videoAspectRatio ) {
			// Pillarboxed (black bars on left/right)
			contentH = elH;
			contentW = elH * videoAspectRatio;
			offsetX = ( elW - contentW ) / 2;
			offsetY = 0;
		} else {
			// Letterboxed (black bars on top/bottom)
			contentW = elW;
			contentH = elW / videoAspectRatio;
			offsetX = 0;
			offsetY = ( elH - contentH ) / 2;
		}

		const result = {
			left: Math.round( offsetX ),
			top: Math.round( offsetY ),
			width: Math.round( contentW ),
			height: Math.round( contentH ),
		};

		return result;
	}

	/**
	 * Create Product hotspot element
	 *
	 * @param {Object} hotspot         - Product Hotspot configuration object
	 * @param {number} containerWidth  - Width of the video container
	 * @param {number} containerHeight - Height of the video container
	 * @param {number} baseWidth       - Base width for calculations
	 * @param {number} baseHeight      - Base height for calculations
	 * @return {HTMLElement} Created hotspot element
	 */
	createProductHotspotElement( hotspot, containerWidth, containerHeight, baseWidth, baseHeight ) {
		const hotspotDiv = document.createElement( 'div' );
		hotspotDiv.classList.add( 'hotspot', 'circle' );
		hotspotDiv.style.position = 'absolute';

		const contentRect = this.computeContentRect();

		// Positioning
		const fallbackPosX = hotspot.oPosition?.x ?? hotspot.position.x;
		const fallbackPosY = hotspot.oPosition?.y ?? hotspot.position.y;

		let fallbackDiameter = hotspot.oSize?.diameter ?? hotspot.size?.diameter ?? 48;
		if ( ! fallbackDiameter ) {
			if ( hotspot.unit === 'percent' && contentRect ) {
				fallbackDiameter = ( PRODUCT_HOTSPOT_CONSTANTS.DEFAULT_DIAMETER_PX / contentRect.width ) * 100;
			} else {
				fallbackDiameter = hotspot.unit === 'percent' ? PRODUCT_HOTSPOT_CONSTANTS.DEFAULT_DIAMETER_PERCENT : PRODUCT_HOTSPOT_CONSTANTS.DEFAULT_DIAMETER_PX;
			}
		}

		let pixelX, pixelY, pixelDiameter;

		if ( hotspot.unit === 'percent' && contentRect ) {
			// New percentage-based positioning
			pixelX = contentRect.left + ( ( fallbackPosX / 100 ) * contentRect.width );
			pixelY = contentRect.top + ( ( fallbackPosY / 100 ) * contentRect.height );
			pixelDiameter = ( fallbackDiameter / 100 ) * contentRect.width;
		} else {
			// Legacy pixel-based positioning (relative to 800x600)
			// We now map these to the contentRect instead of the full container to avoid black bars
			const effectiveRect = contentRect || { left: 0, top: 0, width: containerWidth, height: containerHeight };
			pixelX = effectiveRect.left + ( ( fallbackPosX / baseWidth ) * effectiveRect.width );
			pixelY = effectiveRect.top + ( ( fallbackPosY / baseHeight ) * effectiveRect.height );
			pixelDiameter = ( fallbackDiameter / baseWidth ) * effectiveRect.width;
		}

		hotspotDiv.style.left = `${ pixelX }px`;
		hotspotDiv.style.top = `${ pixelY }px`;
		hotspotDiv.style.width = `${ pixelDiameter }px`;
		hotspotDiv.style.height = `${ pixelDiameter }px`;

		// Background color
		hotspotDiv.style.backgroundColor = ( hotspot.icon || hotspot.customIconUrl ) ? 'white' : ( hotspot.backgroundColor || '#0c80dfa6' );

		// Create content
		const hotspotContent = this.createProductHotspotContent( hotspot, hotspotDiv );
		hotspotDiv.appendChild( hotspotContent );

		return hotspotDiv;
	}

	/**
	 * Create hotspot content
	 *
	 * @param {Object}      hotspot    - Product Hotspot configuration object
	 * @param {HTMLElement} hotspotDiv - Parent hotspot div element
	 * @return {HTMLElement} Created content element
	 */
	createProductHotspotContent( hotspot, hotspotDiv ) {
		const hotspotContent = document.createElement( 'div' );
		hotspotContent.classList.add( 'hotspot-content' );
		hotspotContent.style.position = 'relative';
		hotspotContent.style.width = '100%';
		hotspotContent.style.height = '100%';

		if ( hotspot.icon ) {
			const iconEl = this.createProductHotspotIcon( hotspot.icon );
			hotspotContent.appendChild( iconEl );
		} else if ( hotspot.customIconUrl ) {
			const customIconEl = this.createCustomIcon( hotspot.customIconUrl, hotspot.backgroundColor, hotspotDiv );
			hotspotContent.appendChild( customIconEl );
		} else {
			hotspotContent.classList.add( 'no-icon' );
		}

		const productBoxDiv = this.createProductHotspotProductBox( hotspot );
		hotspotContent.appendChild( productBoxDiv );

		return hotspotContent;
	}

	/**
	 * Create Product hotspot icon
	 *
	 * @param {string} icon - Icon configuration or path
	 * @return {HTMLElement} Created icon element
	 */
	createProductHotspotIcon( icon ) {
		const iconEl = document.createElement( 'i' );
		iconEl.className = `fa-solid fa-${ icon }`;
		iconEl.style.width = '50%';
		iconEl.style.height = '50%';
		iconEl.style.fontSize = '1.6em';
		iconEl.style.display = 'flex';
		iconEl.style.alignItems = 'center';
		iconEl.style.justifyContent = 'center';
		iconEl.style.margin = 'auto';
		iconEl.style.color = '#000';

		return iconEl;
	}

	/**
	 * Create custom icon element
	 *
	 * @param {string}      customIconUrl   - URL of the custom icon
	 * @param {string}      backgroundColor - Background color for fallback
	 * @param {HTMLElement} hotspotDiv      - Parent hotspot div element
	 * @return {HTMLElement} Created custom icon element
	 */
	createCustomIcon( customIconUrl, backgroundColor, hotspotDiv ) {
		const customIconEl = document.createElement( 'img' );
		customIconEl.src = customIconUrl;
		customIconEl.alt = __( 'Custom Icon', 'godam-for-woo' );
		customIconEl.style.width = '50%';
		customIconEl.style.height = '50%';
		customIconEl.style.maxWidth = '100%';
		customIconEl.style.maxHeight = '100%';
		customIconEl.style.objectFit = 'contain';
		customIconEl.style.display = 'block';
		customIconEl.style.margin = 'auto';
		customIconEl.style.pointerEvents = 'none';

		// Add error handling for failed image loads - convert to normal hotspot point
		customIconEl.onerror = function() {
			// Remove the failed image element
			customIconEl.remove();

			// Get the hotspot content container
			const hotspotContent = hotspotDiv?.querySelector( '.hotspot-content' );
			if ( hotspotContent ) {
				// Add no-icon class to show as normal hotspot
				hotspotContent.classList.add( 'no-icon' );
			}

			// Reset hotspot background to default (non-icon) color
			if ( hotspotDiv ) {
				hotspotDiv.style.backgroundColor = backgroundColor || '#0c80dfa6';
			}
		};

		return customIconEl;
	}

	/**
	 * Create Product Hotspot Product Box
	 *
	 * @param {Object} hotspot - Product Hotspot configuration object
	 * @return {HTMLElement} Created product box element
	 */
	createProductHotspotProductBox( hotspot ) {
		// Product box
		const productBoxDiv = document.createElement( 'div' );
		productBoxDiv.classList.add( 'product-hotspot-box' );
		productBoxDiv.style.display = 'flex !important';

		// Placeholder
		const placeholderDiv = document.createElement( 'div' );
		placeholderDiv.classList.add( 'product-hotspot-loading' );

		const SVG_NS = 'http://www.w3.org/2000/svg';

		if ( hotspot?.productId ) {
			const spinnerDiv = document.createElement( 'div' );
			spinnerDiv.classList.add( 'godam-product-hotspot-spinner' );

			// Create SVG
			const svg = document.createElementNS( SVG_NS, 'svg' );
			svg.setAttribute( 'class', 'spinner-svg' );
			svg.setAttribute( 'viewBox', '0 0 50 50' );

			// Create circle
			const circle = document.createElementNS( SVG_NS, 'circle' );
			circle.setAttribute( 'class', 'spinner-circle' );
			circle.setAttribute( 'cx', '25' );
			circle.setAttribute( 'cy', '25' );
			circle.setAttribute( 'r', '20' );
			circle.setAttribute( 'fill', 'none' );
			circle.setAttribute( 'stroke-width', '4' );

			// Append
			svg.appendChild( circle );
			spinnerDiv.appendChild( svg );
			placeholderDiv.appendChild( spinnerDiv );
		} else {
			placeholderDiv.textContent = __( 'No product found', 'godam-for-woo' );
		}

		// Product display
		const productDisplayDiv = document.createElement( 'div' );
		productDisplayDiv.classList.add( 'product-hotspot-woo-display' );

		if ( ! hotspot?.productDetails ) {
			productBoxDiv.appendChild( placeholderDiv );
			return productBoxDiv;
		}

		productBoxDiv.appendChild( productDisplayDiv );

		// Image wrapper
		const imageWrapperDiv = document.createElement( 'div' );
		imageWrapperDiv.classList.add( 'product-hotspot-woo-image-wrapper' );
		productDisplayDiv.appendChild( imageWrapperDiv );

		// Image
		const imageBox = document.createElement( 'img' );
		imageBox.classList.add( 'product-hotspot-woo-image' );
		imageBox.src = hotspot.productDetails.image;
		imageBox.alt = hotspot.productDetails.name;
		imageWrapperDiv.appendChild( imageBox );

		// Product details
		const productDetailsDiv = document.createElement( 'div' );
		productDetailsDiv.classList.add( 'product-hotspot-woo-details' );
		productDisplayDiv.appendChild( productDetailsDiv );

		// Product name
		const productNameDiv = document.createElement( 'div' );
		productNameDiv.classList.add( 'product-hotspot-woo-name' );
		productNameDiv.textContent = hotspot.productDetails.name;
		productDetailsDiv.appendChild( productNameDiv );

		// Redirect image + name to product page if addToCart is false
		if ( ! hotspot.addToCart ) {
			imageBox.style.cursor = 'pointer';
			productNameDiv.style.cursor = 'pointer';

			// Add interactive classes for animation.
			imageBox.classList.add( 'product-hotspot-image-clickable' );
			productNameDiv.classList.add( 'product-hotspot-name-clickable' );

			// Dynamic hover color.
			productNameDiv.addEventListener( 'mouseenter', () => {
				productNameDiv.style.color = hotspot.backgroundColor || '#ab3a6c';
			} );
			productNameDiv.addEventListener( 'mouseleave', () => {
				productNameDiv.style.color = '';
			} );

			const goToProduct = () => {
				window.open( hotspot.productDetails.link, '_blank' );
			};

			imageBox.addEventListener( 'click', goToProduct );
			productNameDiv.addEventListener( 'click', goToProduct );
		}

		// Product price
		const productPriceDiv = document.createElement( 'div' );
		productPriceDiv.classList.add( 'product-hotspot-woo-price' );
		productPriceDiv.innerHTML = hotspot.productDetails.price;
		productDetailsDiv.appendChild( productPriceDiv );

		// Product Action
		const productActionDiv = document.createElement( 'div' );
		productActionDiv.classList.add( 'product-hotspot-action' );

		// --------------------
		// ICON LOGIC
		// --------------------
		const isVariable = hotspot.productDetails?.type === 'variable';
		const isGrouped = hotspot.productDetails?.type === 'grouped';
		const isExternal = hotspot.productDetails?.type === 'external';
		const isOutOfStock = hotspot.productDetails?.in_stock === false;

		const forceProductPage =
			isVariable || isGrouped || isExternal || isOutOfStock;

		// Create icon wrapper
		const iconSpan = document.createElement( 'span' );
		iconSpan.classList.add( 'product-hotspot-icon' );

		// Add rotation class if needed
		if ( forceProductPage || hotspot.addToCart ) {
			iconSpan.classList.add( 'rotate-me' );
		}

		// Use inline SVG (clean + dependency-free)
		iconSpan.innerHTML = forceProductPage || hotspot.addToCart
			? `<svg class="rotate-me" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M21 11H6.414l5.293-5.293l-1.414-1.414L2.586 12l7.707 7.707l1.414-1.414L6.414 13H21z"/></svg>`
			: `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
					<path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
			</svg>`;

		// Always use button with add-to-cart store API; miniCart only controls CSS visibility of the cart widget.
		const productLinkButton = document.createElement( 'button' );
		productLinkButton.classList.add(
			'product-hotspot-woo-link',
			'text-white',
			'flex',
			'items-center',
		);
		productLinkButton.style.background = hotspot.backgroundColor;
		productLinkButton.appendChild( iconSpan );

		// Disable Product Link button during async operation
		productLinkButton.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			productLinkButton.disabled = true;
			productLinkButton.classList.add( 'loading' );

			if ( hotspot.addToCart ) {
				// Redirect to product details page
				window.open( hotspot.productDetails.link, '_blank' );
				productLinkButton.disabled = false;
				productLinkButton.classList.remove( 'loading' );
			} else {
				// Add to cart
				const productId = hotspot.productId;
				const quantity = 1;

				const cartStore = dispatch( 'wc/store/cart' );

				if ( ! cartStore || typeof cartStore.addItemToCart !== 'function' ) {
					// eslint-disable-next-line no-console
					console.error( 'WooCommerce cart store is not available' );
					productLinkButton.disabled = false;
					productLinkButton.classList.remove( 'loading' );
					this.showCartMessage( __( 'WooCommerce cart is not available. Please refresh the page.', 'godam-for-woo' ), 'error' );
					return;
				}

				// Suppress the next broadcast event that third-party floating
				// cart plugins (e.g. ModernCart) listen to. Using { once: true }
				// so it auto-removes after a single event.
				const handler = ( e ) => e.stopImmediatePropagation();
				document.body.addEventListener( 'wc-blocks_added_to_cart', handler, { capture: true, once: true } );

				cartStore
					.addItemToCart( productId, quantity )
					.then( () => {
						this.showCartMessage( __( 'Product added successfully!', 'godam-for-woo' ), 'success' );
					} )
					.catch( ( err ) => {
						// eslint-disable-next-line no-console
						console.error( 'Add to cart failed', err );

						// Check if error code is WooCommerce stock error.
						if ( err?.code === 'woocommerce_rest_product_partially_out_of_stock' ) {
							this.showCartMessage( __( 'Product is partially out of stock.', 'godam-for-woo' ), 'error' );
						} else if ( err?.code === 'woocommerce_rest_product_out_of_stock' ) {
							this.showCartMessage( __( 'Product is out of stock.', 'godam-for-woo' ), 'error' );
						} else {
							this.showCartMessage( __( 'Something went wrong. Try again.', 'godam-for-woo' ), 'error' );
						}
					} )
					.finally( () => {
						productLinkButton.disabled = false;
						productLinkButton.classList.remove( 'loading' );
					} );
			}
		} );
		productActionDiv.appendChild( productLinkButton );
		productDisplayDiv.appendChild( productActionDiv );

		return productBoxDiv;
	}

	// Helper function to show message
	showCartMessage( message, type = 'success' ) {
		let notifArea = document.querySelector( '.godam-notification-area' );
		notifArea = document.createElement( 'div' );
		notifArea.className = 'godam-notification-area';

		const msg = document.createElement( 'div' );
		msg.textContent = message;
		msg.classList.add( 'mini-cart-product-message', type );

		// Dynamic positioning.
		msg.style.position = 'fixed';
		msg.style.top = '50%';
		msg.style.left = '50%';
		msg.style.transform = 'translate(-50%, -50%) scale(0.96)';
		msg.style.zIndex = '999999999999999';

		notifArea.appendChild( msg );

		const container = this.player.el();
		container.appendChild( notifArea );

		requestAnimationFrame( () => {
			requestAnimationFrame( () => {
				msg.style.opacity = '1';
				msg.style.transform = 'translate(-50%, -50%) scale(1)';
			} );
		} );

		// Fade out and remove after 2s.
		setTimeout( () => {
			msg.style.opacity = '0';
			msg.style.transform = 'translate(-50%, -50%) scale(0.96)';
			setTimeout( () => {
				msg.remove();
				notifArea.remove();
			}, 300 );
		}, 2000 );
	}

	/**
	 * Setup hotspot hover events
	 *
	 * @param {HTMLElement} hotspotDiv - The hotspot element to add hover events to
	 */
	setupProductHotspotHoverEvents( hotspotDiv ) {
		hotspotDiv.addEventListener( 'mouseenter', () => {
			this.wasPlayingBeforeHover = ! this.player.paused();
			this.player.pause();
		} );

		hotspotDiv.addEventListener( 'mouseleave', () => {
			if ( this.wasPlayingBeforeHover ) {
				this.player.play();
			}
		} );
	}

	/**
	 * Position product box relative to hotspot, constrained within the video container
	 *
	 * @param {HTMLElement} hotspotDiv    - The product hotspot element for positioning reference
	 * @param {HTMLElement} productBoxDiv - The product box element to position
	 */
	positionProductBox( hotspotDiv, productBoxDiv ) {
		const videoContainer = this.player.el();
		const containerRect = videoContainer.getBoundingClientRect();
		const hotspotRect = hotspotDiv.getBoundingClientRect();

		// Temporarily make tooltip visible to measure it accurately
		const originalVisibility = productBoxDiv.style.visibility;
		const originalOpacity = productBoxDiv.style.opacity;
		productBoxDiv.style.visibility = 'hidden';
		productBoxDiv.style.opacity = '0';
		productBoxDiv.style.display = 'block';

		const productBoxRect = productBoxDiv.getBoundingClientRect();

		// Restore original styles
		productBoxDiv.style.visibility = originalVisibility;
		productBoxDiv.style.opacity = originalOpacity;

		// Calculate space relative to video container (not viewport)
		const spaceAbove = hotspotRect.top - containerRect.top;
		const spaceBelow = containerRect.bottom - hotspotRect.bottom;

		const productBoxHeight = productBoxRect.height;
		const productBoxWidth = productBoxRect.width;

		// Minimum padding from container edges
		const edgePadding = 8;

		// Reset all positioning classes and styles first
		productBoxDiv.classList.remove( 'tooltip-top', 'tooltip-bottom', 'tooltip-left', 'tooltip-right', 'no-arrow', 'product-bottom' );
		productBoxDiv.style.top = '';
		productBoxDiv.style.bottom = '';
		productBoxDiv.style.left = '';
		productBoxDiv.style.right = '';
		productBoxDiv.style.transform = '';

		// Vertical positioning - prefer above, fallback to below
		if ( spaceAbove >= productBoxHeight + edgePadding ) {
			// Place above
			productBoxDiv.style.bottom = '100%';
			productBoxDiv.style.top = 'auto';
			productBoxDiv.classList.add( 'tooltip-top' );
		} else if ( spaceBelow >= productBoxHeight + edgePadding ) {
			// Place below
			productBoxDiv.style.bottom = 'auto';
			productBoxDiv.style.top = '100%';
			productBoxDiv.classList.add( 'product-bottom' );
		} else if ( spaceAbove >= spaceBelow ) {
			productBoxDiv.style.bottom = '100%';
			productBoxDiv.style.top = 'auto';
			productBoxDiv.classList.add( 'tooltip-top' );
		} else {
			productBoxDiv.style.bottom = 'auto';
			productBoxDiv.style.top = '100%';
			productBoxDiv.classList.add( 'product-bottom' );
		}

		// Horizontal positioning - calculate where tooltip would overflow
		const hotspotCenterInContainer = ( hotspotRect.left + ( hotspotRect.width / 2 ) ) - containerRect.left;
		const productBoxHalfWidth = productBoxWidth / 2;

		// Check if centered productBox would overflow left or right of container
		const wouldOverflowLeft = ( hotspotCenterInContainer - productBoxHalfWidth ) < edgePadding;
		const wouldOverflowRight = ( hotspotCenterInContainer + productBoxHalfWidth ) > ( containerRect.width - edgePadding );

		if ( wouldOverflowLeft && wouldOverflowRight ) {
			// Tooltip is wider than available space, center it as best as possible
			productBoxDiv.style.left = '50%';
			productBoxDiv.style.transform = 'translateX(-50%)';
			productBoxDiv.classList.add( 'no-arrow' );
		} else if ( wouldOverflowLeft ) {
			// Align tooltip to the left edge of hotspot, but ensure it stays within container
			const leftOffset = Math.max( edgePadding - ( hotspotRect.left - containerRect.left ), 0 );
			productBoxDiv.style.left = `${ leftOffset }px`;
			productBoxDiv.style.right = 'auto';
			productBoxDiv.style.transform = 'translateX(0)';
			productBoxDiv.classList.add( 'tooltip-left', 'no-arrow' );
		} else if ( wouldOverflowRight ) {
			// Align tooltip to the right edge of hotspot, but ensure it stays within container
			const rightOffset = Math.max( edgePadding - ( containerRect.right - hotspotRect.right ), 0 );
			productBoxDiv.style.left = 'auto';
			productBoxDiv.style.right = `${ rightOffset }px`;
			productBoxDiv.style.transform = 'translateX(0)';
			productBoxDiv.classList.add( 'tooltip-right', 'no-arrow' );
		} else {
			// Centered horizontally - tooltip fits within container
			productBoxDiv.style.left = '50%';
			productBoxDiv.style.right = 'auto';
			productBoxDiv.style.transform = 'translateX(-50%)';
		}
	}

	/**
	 * Update hotspot positions on resize
	 */
	updateProductHotspotPositions() {
		const videoContainer = this.player.el();
		const containerWidth = videoContainer?.offsetWidth;
		const containerHeight = videoContainer?.offsetHeight;

		const baseWidth = WooCommerceLayerManager.BASE_WIDTH;
		const baseHeight = WooCommerceLayerManager.BASE_HEIGHT;

		const contentRect = this.computeContentRect();

		this.wooLayers.forEach( ( layerObj ) => {
			const isLayerHidden = layerObj.layerElement.classList.contains( 'hidden' );
			const hotspotDivs = layerObj.layerElement.querySelectorAll( '.hotspot' );
			hotspotDivs.forEach( ( hotspotDiv, index ) => {
				const hotspot = layerObj.productHotspots[ index ];

				// Recalc position
				const fallbackPosX = hotspot.oPosition?.x ?? hotspot.position.x;
				const fallbackPosY = hotspot.oPosition?.y ?? hotspot.position.y;
				const fallbackDiameter = hotspot.oSize?.diameter ?? hotspot.size?.diameter ?? 48;

				let pixelX, pixelY, pixelDiameter;

				if ( hotspot.unit === 'percent' && contentRect ) {
					// New percentage-based positioning
					pixelX = contentRect.left + ( ( fallbackPosX / 100 ) * contentRect.width );
					pixelY = contentRect.top + ( ( fallbackPosY / 100 ) * contentRect.height );
					pixelDiameter = ( fallbackDiameter / 100 ) * contentRect.width;
				} else {
					// Legacy pixel-based positioning
					// We now map these to the contentRect instead of the full container to avoid black bars
					const effectiveRect = contentRect || { left: 0, top: 0, width: containerWidth, height: containerHeight };
					pixelX = effectiveRect.left + ( ( fallbackPosX / baseWidth ) * effectiveRect.width );
					pixelY = effectiveRect.top + ( ( fallbackPosY / baseHeight ) * effectiveRect.height );
					pixelDiameter = ( fallbackDiameter / baseWidth ) * effectiveRect.width;
				}

				hotspotDiv.style.left = `${ pixelX }px`;
				hotspotDiv.style.top = `${ pixelY }px`;
				hotspotDiv.style.width = `${ pixelDiameter }px`;
				hotspotDiv.style.height = `${ pixelDiameter }px`;

				// Product Box geometry relies on measurable layout; skip while layer is hidden.
				if ( isLayerHidden ) {
					return;
				}

				const productBoxDiv = hotspotDiv.querySelector( '.product-hotspot-box' );
				if ( productBoxDiv ) {
					requestAnimationFrame( () => {
						this.positionProductBox( hotspotDiv, productBoxDiv );
					} );
				}
			} );
		} );
	}

	/**
	 * Reset WooCommerce hotspot layer state
	 */
	reset() {
		this.wooLayers = [];
		this.wasPlayingBeforeHover = false;
	}
}
